import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import {NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CustomFormComponent} from '../../classes/custom-form-component';

@Component({
    selector: 'number-input',
    templateUrl: './number-input.component.html',
    styleUrls: ['./number-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumberInputComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NumberInputComponent),
            multi: true,
        },
    ],
})
export class NumberInputComponent extends CustomFormComponent<number> implements OnInit {
    @ViewChild('inputEl', {static: true}) $inputEl: ElementRef<HTMLInputElement>;
    @Input() step: number;
    @Input() min: number;
    @Input() max: number;
    @Input() symbol: string;

    @Output() inputBlur = new EventEmitter();
    @Output() incrementalChange = new EventEmitter<number>();

    constructor($el: ElementRef, cd: ChangeDetectorRef) {
        super($el, cd);
    }

    ngOnInit() {
        this.step = this.step || 1;
        this.initInputValue('min', null);
        this.initInputValue('max', null);
    }

    get value(): number {
        return this.internalValue;
    }

    set value(v: number) {
        if (isFinite(v)) {
            const valueOrPlaceholder = v !== 0 ? v || this.placeholder || 0 : v;
            this.getInput().value = String(valueOrPlaceholder);
        }
    }

    writeValue(value: number) {
        super.writeValue(value);
        this.value = value;
    }

    isInvalid() {
        return !this.getInput().validity.valid;
    }

    getInput() {
        return this.$inputEl.nativeElement;
    }

    onChange() {
        let stringValue = this.getInput().value.trim().replace(/,/, '.');
        if (stringValue) {
            this.setValue(parseFloat(stringValue));
        } else {
            this.invokeChangeCallback(undefined);
        }
    }

    onBlur() {
        this.invokeTouchCallback();
        this.inputBlur.emit(null);
    }

    increment() {
        let newValue = (this.value || 0) + Number(this.step);
        if (this.max !== null && newValue > Number(this.max)) {
            newValue = Number(this.max);
        }
        this.setValue(newValue);
        this.incrementalChange.emit(newValue);
        this.invokeTouchCallback();
    }

    decrement() {
        let newValue: number = (this.value || 0) - Number(this.step);
        if (this.min !== null && newValue < Number(this.min)) {
            newValue = Number(this.min);
        }
        this.setValue(newValue);
        this.incrementalChange.emit(newValue);
        this.invokeTouchCallback();
    }

    invokeChangeCallback(newValue) {
        if (newValue !== undefined) {
            newValue = this.normalizeValue(newValue);
            this.value = newValue;
        }
        super.invokeChangeCallback(newValue);
    }

    private normalizeValue(value: number): number {
        return Number(value.toFixed(this.getFloatPartLength()));
    }

    private getFloatPartLength(): number {
        let stepString = this.step.toString();
        if (stepString.match(/\./)) {
            return stepString.match(/\.(\d+)/)[1].length;
        }
        return 0;
    }

    private setValue(value: number) {
        this.invokeChangeCallback(value);
    }
}
